// The real Attendance Allowance questions, loaded from KareHero's own
// normalised schema — the seed JSON the call-to-claim prototype already uses
// to fill the government PDF.
//
// Why this rather than reading the PDF: the PDF says nothing about what is
// required. It carries 473 AcroForm fields and not one of them has the
// Required flag set, so "which fields must be answered" is simply not in the
// document. It lives in this schema instead: `isRequired` per question, plus
// the DWP guidance text and the conditional reveals.
//
// NOTE ON IMPORT DIRECTION: the schema currently sits under
// james-playground/call-to-claim/schema, so this reaches sideways across
// product areas. It is data rather than components, and it is loaded in one
// place on purpose — if the schema moves somewhere shared, this file is the
// only thing to repoint.

type RawSub = {
  condition?: { value?: unknown; operator?: string };
  question?: RawQuestion;
};

type RawOption = { id: string; label: string };

type RawQuestion = {
  id: string;
  label: string;
  isRequired?: boolean;
  placeholder?: string;
  field?: { type?: string; options?: RawOption[] };
  subQuestions?: RawSub[];
};

/** The control the schema asks for. Drives which chat pattern can ask it. */
export type AaFieldType =
  | 'text'
  | 'date'
  | 'select'
  | 'address'
  | 'boolean'
  | 'question-table'
  | 'slider-select'
  | 'multi-select-box'
  | 'number'
  | 'none';

export type AaQuestion = {
  id: string;
  label: string;
  required: boolean;
  /** What the form uses to take the answer. */
  type: AaFieldType;
  /** The answers it offers, where it offers any. */
  options: string[];
  /** DWP's own guidance under the question, where it has any. */
  guidance?: string;
  /** How deep this sits under its top-level question. 0 = top level. */
  depth: number;
  /**
   * Why this only sometimes gets asked, as a full sentence naming the answer
   * that opens it. "if yes" on its own says yes to what — the reader has to
   * hold the row above in their head to make sense of it.
   */
  revealedBy?: string;
};

// The answer that opens a follow-up, in words. Option ids are resolved against
// the parent question's own options, so the tag reads "Only asked if the
// answer above is 3+" rather than echoing a slug.
const describeCondition = (
  value: unknown,
  operator: string | undefined,
  parentOptions: RawOption[],
): string | undefined => {
  const negated = operator === 'not-equal';
  let answer: string | undefined;

  if (value === true) answer = 'Yes';
  else if (value === false) answer = 'No';
  else if (typeof value === 'string') {
    answer = parentOptions.find((o) => o.id === value)?.label;
  }
  if (!answer) return undefined;

  return negated
    ? `Only asked if the answer above is not ${answer}`
    : `Only asked if the answer above is ${answer}`;
};

// Flattens a question and everything it reveals into one ordered list, keeping
// depth so the nesting stays visible. The form's branching is the thing A3
// argues about — hiding it behind a count made the table shorter and the
// argument weaker.
const flatten = (
  q: RawQuestion,
  depth = 0,
  revealedBy?: string,
): AaQuestion[] => [
  {
    id: q.id,
    label: q.label,
    required: q.isRequired === true,
    type: (q.field?.type as AaFieldType) ?? 'none',
    options: (q.field?.options ?? []).map((o) => o.label),
    guidance: q.placeholder,
    depth,
    revealedBy,
  },
  ...(q.subQuestions ?? []).flatMap((sub) =>
    sub.question
      ? flatten(
          sub.question,
          depth + 1,
          describeCondition(
            sub.condition?.value,
            sub.condition?.operator,
            q.field?.options ?? [],
          ),
        )
      : [],
  ),
];

const modules = import.meta.glob(
  '../../../james-playground/call-to-claim/schema/*.json',
  { eager: true },
) as Record<string, { default: RawQuestion[] }>;

const bySection: Record<string, AaQuestion[]> = {};
for (const [path, mod] of Object.entries(modules)) {
  const name = path
    .split('/')
    .pop()!
    .replace(/\.json$/, '');
  const raw = mod.default ?? (mod as unknown as RawQuestion[]);
  bySection[name] = raw.flatMap((q) => flatten(q));
}

export const aaQuestions = (section: string): AaQuestion[] =>
  bySection[section] ?? [];

/**
 * A section's name as a person would say it: the file's number and hyphens
 * off, so `5-help-with-care-needs-during-the-day` reads as what it is.
 *
 * Here rather than in a caller, because two of them want it and the schema's
 * file names are the only thing that decides it.
 */
export const aaSectionName = (section: string): string =>
  section.replace(/^\d+(-\d+)?-/, '').replace(/-/g, ' ');

/** The sections, in the order the form asks them. */
export const AA_SECTION_IDS = Object.keys(bySection).sort((a, b) => {
  const n = (id: string) => id.split('-').slice(0, 2).map(Number);
  const [aMajor, aMinor] = n(a);
  const [bMajor, bMinor] = n(b);
  return aMajor - bMajor || (aMinor || 0) - (bMinor || 0);
});

/** Every question in the form, in section order, each tagged with its section. */
export const aaAllQuestions = (): (AaQuestion & { section: string })[] =>
  AA_SECTION_IDS.flatMap((section) =>
    (bySection[section] ?? []).map((q) => ({ ...q, section })),
  );

// Totals across the whole form, for the section's own caption.
export const AA_TOTALS = Object.values(bySection).reduce(
  (acc, qs) => ({
    questions: acc.questions + qs.length,
    required: acc.required + qs.filter((q) => q.required).length,
  }),
  { questions: 0, required: 0 },
);
